import { useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  ChevronDownIcon,
  BuildingOffice2Icon,
  BeakerIcon,
  CogIcon,
  UserIcon,
} from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { useUpdateTokenRole } from "../../custom-hooks/queries";
import { useDecodedToken } from "../../custom-hooks/useToken";
import { useClerk, useUser } from "@clerk/clerk-react";
import { vendorRightsList, rightsList } from "../../utils/constants";
import LoadingModal from "../Skeletons/LoadingModal";

export default function DualRole({ clerkUserId }) {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { mutate: updateRole } = useUpdateTokenRole();
  const { decoded } = useDecodedToken();
  const { session } = useClerk();
  const { user } = useUser();

  const subRole = decoded?.sub_role || [];
  const rights = decoded?.rights || [];
  const currentRole = (
    user?.publicMetadata?.role ||
    decoded?.role ||
    ""
  ).toString();
  const isCurrentSponsor = currentRole.includes("Sponsor");
  const isCurrentProvider = currentRole.includes("Provider");
  const isCurrentCRO = currentRole.includes("CRO");
  const isCurrentAdmin =
    currentRole.includes("Admin") || currentRole.includes("Diligent");

  // Get user name and organization from decoded token or user object
  const userName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : decoded?.name || user?.fullName || "User";
  const sponsorOrg =
    decoded?.sponsor_name || decoded?.organization_name || "Organization";
  const vendorOrg =
    decoded?.provider_name || decoded?.vendor_name || "Vendor Organization";
  const croOrg = decoded?.cro_name || "CRO Organization";
  const adminOrg = decoded?.admin_org || "Diligent Pharma";

  const handleRoleChange = (role) => {
    if (!clerkUserId) {
      console.error("Clerk user ID not found");
      return;
    }

    setIsLoading(true);
    sessionStorage.removeItem("sso_state");

    // Helper: generate random GUID
    const generateGuid = () =>
      "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });

    // (No deterministic conversion of Clerk ID to GUID is performed)

    if (role === "Provider User") {
      const finalSubRole = ["Vendor Admin"];
      const finalRights = vendorRightsList;
      updateRole(
        { id: clerkUserId, role, sub_role: finalSubRole, rights: finalRights },
        {
          onSuccess: async () => {
            await session.reload();
            setIsLoading(false);
            window.location.reload();
            navigate("/");
          },
          onError: (error) => {
            console.error("Error updating role:", error);
            setIsLoading(false);
          },
        }
      );
      return;
    }

    // If switching to Sponsor Portal, call updateRole with sub_role and rights
    if (role === "Sponsor User") {
      const finalSubRole = ["Sponsor Admin"];

      // Merge decoded rights, vendor rights, and global rights list to form a comprehensive set
      const combinedRights = Array.from(
        new Set([...(rights || []), ...vendorRightsList, ...rightsList])
      );

      updateRole(
        {
          id: clerkUserId,
          role: "Sponsor User",
          sub_role: finalSubRole,
          rights: combinedRights,
        },
        {
          onSuccess: async () => {
            await session.reload();
            setIsLoading(false);
            window.location.reload();
            navigate("/");
          },
          onError: (error) => {
            console.error("Error updating role:", error);
            setIsLoading(false);
          },
        }
      );
      return;
    }

    // Handle CRO User role
    if (role === "CRO User") {
      const finalSubRole =
        subRole && subRole.length > 0 ? subRole : ["CRO Admin"];
      const finalRights = rights && rights.length > 0 ? rights : rightsList;
      updateRole(
        { id: clerkUserId, role, sub_role: finalSubRole, rights: finalRights },
        {
          onSuccess: async () => {
            await session.reload();
            setIsLoading(false);
            navigate("/");
          },
          onError: (error) => {
            console.error("Error updating role:", error);
            setIsLoading(false);
          },
        }
      );
      return;
    }

    // Handle Diligent Admin role
    if (role === "Diligent Admin") {
      const finalSubRole = subRole && subRole.length > 0 ? subRole : ["Admin"];
      const finalRights = Array.from(
        new Set([...(rights || []), ...vendorRightsList, ...rightsList])
      );
      updateRole(
        { id: clerkUserId, role, sub_role: finalSubRole, rights: finalRights },
        {
          onSuccess: async () => {
            await session.reload();
            setIsLoading(false);
            navigate("/");
          },
          onError: (error) => {
            console.error("Error updating role:", error);
            setIsLoading(false);
          },
        }
      );
      return;
    }

    // Fallback behavior for other roles
    const finalSubRole = subRole;
    const finalRights = rights;
    updateRole(
      { id: clerkUserId, role, sub_role: finalSubRole, rights: finalRights },
      {
        onSuccess: async () => {
          await session.reload();
          setIsLoading(false);
          navigate("/");
        },
        onError: (error) => {
          console.error("Error updating role:", error);
          setIsLoading(false);
        },
      }
    );
  };

  // Define role items matching the image design
  const roleItems = [
    {
      role: "Sponsor User",
      label: "Sponsor User",
      icon: BuildingOffice2Icon,
      isCurrent: isCurrentSponsor,
      userName: userName,
      orgName: sponsorOrg,
      onClick: () => handleRoleChange("Sponsor User"),
    },
    {
      role: "Provider User",
      label: "Vendor User",
      icon: BuildingOffice2Icon,
      isCurrent: isCurrentProvider,
      userName: userName,
      orgName: vendorOrg,
      onClick: () => handleRoleChange("Provider User"),
    },
  ];

  return (
    <>
      <LoadingModal open={isLoading} />
      <Menu as="div" className="relative inline-block ml-2">
        {/* ======================= TOP BUTTON ======================= */}
        <MenuButton className="inline-flex items-center gap-1 rounded-md px-3 py-2 bg-transparent hover:bg-gray-50">
          <span className="text-[15px] font-semibold text-[#5A2ABF]">
            {isCurrentSponsor ? "360" : "RESPOND"}
          </span>
          <ChevronDownIcon className="h-4 w-4 text-[#5A2ABF]" />
        </MenuButton>

        {/* ======================= DROPDOWN ======================= */}
        <MenuItems className="absolute right-0 mt-2 w-[340px] rounded-md bg-white shadow-[0_4px_14px_rgba(0,0,0,0.12)] ring-1 ring-black/5 focus:outline-none overflow-hidden">
          {/* HEADER */}
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-[11px] font-semibold text-gray-500 tracking-wide">
              SWITCH PORTAL
            </p>
          </div>

          {/* ITEMS */}
          <div className="py-1">
            {roleItems.map((item) => {
              const isCurrent = item.isCurrent;
              const isSponsor = item.label.includes("Sponsor");

              return (
                <MenuItem key={item.role}>
                  {({ active }) => (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        item.onClick();
                      }}
                      className={`
                      w-full flex items-center justify-between px-4 py-4
                      ${
                        isCurrent
                          ? "bg-[#F5EEFF]"
                          : active
                          ? "bg-gray-50"
                          : "bg-white"
                      }
                      transition
                    `}
                    >
                      {/* LEFT SIDE */}
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-[#444] text-[15px] tracking-tight flex items-center">
                          <span className="text-[#5A2ABF] font-semibold mr-2">
                            Diligent
                          </span>

                          <span className="h-5 border-l border-gray-300 mx-1"></span>

                          <span className="text-[#5A2ABF] font-semibold">
                            {isSponsor ? "360" : "RESPOND"}
                          </span>
                        </span>

                        {/* PORTAL NAME */}
                        <span className="text-[12px] text-gray-800 font-medium ml-1">
                          {isSponsor ? "Sponsor Portal" : "Vendor Portal"}
                        </span>
                      </div>

                      {/* CURRENT BADGE */}
                      {isCurrent && (
                        <span className="px-3 py-0.5 text-[12px] font-medium rounded-full bg-purple-100 text-[#5A2ABF] ml-2">
                          Current
                        </span>
                      )}
                    </button>
                  )}
                </MenuItem>
              );
            })}
          </div>
        </MenuItems>
      </Menu>
    </>
  );
}
